/**
 * Soruların resim ve seslerini oturum başında indirme (8 Eki 2026).
 *
 * Eskiden her sorunun resmi/sesi o soru ekrana gelince indiriliyordu; okul wifi'si o anda koparsa çocuk boş
 * resim görüyor ya da ses açılmıyordu. Artık oynatıcı açılırken kalan soruların dosyaları indirilir, tarayıcı
 * önbelleğe alır; sonra soru ekrana geldiğinde ağa gerek kalmaz. Dosyalar Azure Blob depolamadan gelir, API
 * sunucusuna yük bindirmez. En fazla HAZIRLIK_AZAMI_MS beklenir; yetişmeyen dosya yine eskisi gibi o soruda yüklenir.
 */
import type { SessionItem } from "../mechanics/types";

export const HAZIRLIK_AZAMI_MS = 20000;
/** Aynı anda en fazla bu kadar dosya indirilir (okul ağını boğmasın). */
const ES_ZAMANLI = 6;

const MEDYA = /\.(png|jpe?g|webp|gif|svg|mp3|m4a|wav|ogg)(\?|#|$)/i;
const SES = /\.(mp3|m4a|wav|ogg)(\?|#|$)/i;

/** İçerikteki resim/ses adresleri (…Url alanları, "images" dizileri ve medya uzantılı her metin). */
export function collectAssetUrls(items: SessionItem[]): string[] {
  const set = new Set<string>();
  const gez = (v: unknown, key: string) => {
    if (typeof v === "string") {
      const s = v.trim();
      if (s && (key.endsWith("Url") || key === "images" || MEDYA.test(s)) && !s.startsWith("data:")) set.add(s);
      return;
    }
    if (Array.isArray(v)) {
      for (const x of v) gez(x, key);
      return;
    }
    if (v && typeof v === "object") for (const [k, x] of Object.entries(v)) gez(x, k);
  };
  for (const it of items) gez(it.content, "");
  return [...set];
}

// İndirilen resim nesneleri tutulur; tarayıcı bellekteki kopyayı erkenden atmasın.
const tutulan: HTMLImageElement[] = [];

function indir(url: string): Promise<void> {
  if (SES.test(url)) {
    // Ses: tarayıcı önbelleğini ısıtır (CORS gerektirmez); başarısızlık önemsiz, soru yine kendi yükler.
    return fetch(url, { mode: "no-cors", cache: "force-cache" }).then(() => undefined, () => undefined);
  }
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve();
    img.onerror = () => resolve();
    img.src = url;
    tutulan.push(img);
  });
}

/** Dosyaları indirir; ilerlemeyi bildirir. Hepsi bitince ya da süre dolunca döner. */
export async function preloadAssets(
  urls: string[],
  onProgress: (bitti: number, toplam: number) => void,
  azamiMs = HAZIRLIK_AZAMI_MS,
): Promise<void> {
  if (urls.length === 0) return;
  let bitti = 0;
  let sira = 0;
  onProgress(0, urls.length);
  const isci = async () => {
    while (sira < urls.length) {
      const url = urls[sira++]!;
      await indir(url);
      bitti++;
      onProgress(bitti, urls.length);
    }
  };
  const hepsi = Promise.all(Array.from({ length: Math.min(ES_ZAMANLI, urls.length) }, isci));
  await Promise.race([hepsi, new Promise((r) => setTimeout(r, azamiMs))]);
}
